import * as THREE from 'three';
import {
  applyLaneOffsetOnClosedPath,
  isPatrolPaused,
  poseOnClosedPath,
  travelFacing,
  walkedTime,
  type PathPoint,
} from '../../game/visitorLocomotion';
import { bakeColored, mergeBufferGeometries } from './batch';
import { mesh, roundedBox } from './helpers';
import type { ParkMaterials } from './materials';
import type { QueueSlot } from './queue';

export { closedPathMetrics, poseOnClosedPath, type PathPoint } from '../../game/visitorLocomotion';

const SHIRTS = [0x4f9ddb, 0xe5514f, 0x3fb8a8, 0xf7d35c, 0x9a6fc9, 0xf08a3c, 0xf0879e, 0xfff6e6, 0x5cb85a, 0x2f6fb5];
const PANTS = [0x2f3f6a, 0x5a3a2a, 0x3c5a8a, 0xc9b28a, 0x4a4a4a, 0x7a4a6a];
const SKINS = [0xf5c9a6, 0xe8b08a, 0xc98a5c, 0x8d5a3c, 0xf8d7c0];
const HAIRS = [0x3b2314, 0x8b5a34, 0xe8c070, 0xd9534f, 0x1e1e1e, 0x6b4a3a];
const CAPS = [0xe5514f, 0x4f9ddb, 0xf7d35c];
const SUN_HAT = 0xf2dca0;
const BALLOONS = [0xe5514f, 0x4f9ddb, 0xf7d35c, 0xf0879e, 0x5cb85a];

/**
 * Fable toy-figure proportions (unit height, scaled per guest by `Look.height`):
 * stubby legs with rounded shoes, an egg-shaped one-colour torso with stub arms,
 * an oversized head and a hair/hat cap. Body art reads facing local -Z.
 */
export const VISITOR_LEG_TOP = 0.2;
export const VISITOR_TORSO_TOP = 0.55;
export const VISITOR_HEAD_Y = 0.66;
export const VISITOR_HEAD_RADIUS = 0.115;
const VISITOR_HAND_OFFSET: [number, number, number] = [0.165, -0.375, -0.02];
const VISITOR_CAP_BRIM_Z = -0.115;

export type Route = {
  points: THREE.Vector2[];
  speed: number;
  offset: number;
  lane?: number;
  pauseEvery?: number;
  pauseFor?: number;
  pausePhase?: number;
};
export type Standing = { x: number; z: number; facing: number };

/**
 * Body art reads facing local -Z. Route walkers add π so the visible front follows the
 * segment tangent. Queue/standing callers keep their existing yaw contract.
 */
export const ROUTE_WALKER_YAW_OFFSET = Math.PI;

export function routeWalkerVisualYaw(tangentYaw: number): number {
  return tangentYaw + ROUTE_WALKER_YAW_OFFSET;
}

/** Accepted Fable family before COL-008 global polish. */
export const VISITOR_FAMILY_CHILD_LOOK_SCALE = 0.9;
export const VISITOR_FAMILY_ADULT_LOOK_SCALES = [0.98, 1.01, 1.04] as const;
/** Modest ~8% shrink so the toy-figure family sits closer to paths/props. */
export const VISITOR_GLOBAL_LOOK_SCALE = 0.92;

/** Child / adult look scale after the COL-008 global factor. */
export const VISITOR_CHILD_LOOK_SCALE = VISITOR_FAMILY_CHILD_LOOK_SCALE * VISITOR_GLOBAL_LOOK_SCALE;
export const VISITOR_ADULT_LOOK_SCALES: readonly [number, number, number] = [
  VISITOR_FAMILY_ADULT_LOOK_SCALES[0] * VISITOR_GLOBAL_LOOK_SCALE,
  VISITOR_FAMILY_ADULT_LOOK_SCALES[1] * VISITOR_GLOBAL_LOOK_SCALE,
  VISITOR_FAMILY_ADULT_LOOK_SCALES[2] * VISITOR_GLOBAL_LOOK_SCALE,
];

export function visitorLookScale(index: number): number {
  if (index % 4 === 3) {
    return VISITOR_CHILD_LOOK_SCALE;
  }
  return VISITOR_ADULT_LOOK_SCALES[index % VISITOR_ADULT_LOOK_SCALES.length]!;
}

type Hat = 'none' | 'cap' | 'sun';

type Look = {
  shirt: number;
  pants: number;
  skin: number;
  hair: number;
  hairScale: [number, number, number];
  hairLift: number;
  hat: Hat;
  height: number;
  balloon: number | null;
};

function lookFor(index: number, allowBalloon: boolean): Look {
  const style = index % 3;
  const isKid = index % 4 === 3;
  const capped = index % 5 === 2;
  const sunHat = !capped && !isKid && index % 7 === 4;
  const hat: Hat = capped ? 'cap' : sunHat ? 'sun' : 'none';
  return {
    shirt: SHIRTS[index % SHIRTS.length]!,
    pants: PANTS[(index * 7) % PANTS.length]!,
    skin: SKINS[(index * 3) % SKINS.length]!,
    hair: capped ? CAPS[index % CAPS.length]! : sunHat ? SUN_HAT : HAIRS[(index * 5) % HAIRS.length]!,
    // Short crop / bob that hangs / swept back with a longer nape; caps sit high and shallow,
    // sun hats are a low straw dome over the wide brim.
    hairScale: capped
      ? [1.1, 0.72, 1.1]
      : sunHat
        ? [1.14, 0.66, 1.14]
        : style === 0
          ? [1.05, 0.7, 1.05]
          : style === 1
            ? [1.08, 1.04, 1.1]
            : [1.04, 0.84, 1.18],
    hairLift: capped ? 0.03 : sunHat ? 0.045 : style === 1 ? -0.035 : 0.005,
    hat,
    height: visitorLookScale(index),
    balloon: allowBalloon && index % 4 === 1 ? BALLOONS[index % BALLOONS.length]! : null,
  };
}

/** Bake swatches: white takes the per-guest instance tint, greys shade that tint. */
function swatch(value: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: value });
}

/** Stubby leg with a rounded shoe; pants tint comes from the instance colour, shoe reads as its dark shade. */
function makeLegProto(x: number, mats: ParkMaterials): THREE.Group {
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(0.05, 0.054, 0.16, 8), mats.horseCoat, { position: [x, 0.13, 0] }));
  proto.add(mesh(roundedBox(0.105, 0.065, 0.15, 0.03), mats.charcoal, { position: [x, 0.033, -0.015] }));
  return proto;
}

/** Egg-shaped one-colour torso with two stub arms; lathe keeps it round from every yaw. */
function makeTorsoProto(mats: ParkMaterials): THREE.Group {
  const proto = new THREE.Group();
  const profile = [
    new THREE.Vector2(0, VISITOR_LEG_TOP - 0.01),
    new THREE.Vector2(0.095, VISITOR_LEG_TOP - 0.01),
    new THREE.Vector2(0.13, 0.24),
    new THREE.Vector2(0.14, 0.32),
    new THREE.Vector2(0.13, 0.42),
    new THREE.Vector2(0.1, 0.5),
    new THREE.Vector2(0.06, VISITOR_TORSO_TOP - 0.005),
    new THREE.Vector2(0, VISITOR_TORSO_TOP),
  ];
  proto.add(mesh(new THREE.LatheGeometry(profile, 12), mats.horseCoat));
  const sleeve = swatch(0xe6e6e6);
  for (const side of [-1, 1]) {
    proto.add(
      mesh(new THREE.CapsuleGeometry(0.034, 0.13, 3, 6), sleeve, {
        position: [side * 0.145, 0.38, -0.01],
        rotation: [0.06, 0, side * 0.18],
      }),
    );
  }
  return proto;
}

/** Head sphere plus both hands in one geometry so hands take the skin tint for free. */
function makeHeadGeometry(): THREE.BufferGeometry {
  const parts = [new THREE.SphereGeometry(VISITOR_HEAD_RADIUS, 9, 7)];
  for (const side of [-1, 1]) {
    const hand = new THREE.SphereGeometry(0.04, 5, 4);
    hand.translate(side * VISITOR_HAND_OFFSET[0], VISITOR_HAND_OFFSET[1], VISITOR_HAND_OFFSET[2]);
    parts.push(hand);
  }
  const merged = mergeBufferGeometries(parts);
  parts.forEach((geometry) => geometry.dispose());
  return merged;
}

/**
 * Stylized toy-figure guests. Left/right legs are separate InstancedMeshes so a walking
 * stride is visible; remaining parts stay one instanced mesh each (torso, head+hands,
 * hair/hat dome, hat brims, balloons, strings).
 */
export function createVisitors(
  mats: ParkMaterials,
  parkTop: number,
  spec: { routes: Route[]; standing: Standing[]; queue: QueueSlot[] },
): THREE.Group {
  const group = new THREE.Group();
  const count = spec.routes.length + spec.standing.length + spec.queue.length;
  const looks: Look[] = [];
  for (let i = 0; i < count; i += 1) {
    looks.push(lookFor(i, i < spec.routes.length + spec.standing.length));
  }

  const white = mats.horseCoat.clone();
  const leftLegs = new THREE.InstancedMesh(bakeColored(makeLegProto(-0.055, mats)), mats.baked, count);
  const rightLegs = new THREE.InstancedMesh(bakeColored(makeLegProto(0.055, mats)), mats.baked, count);
  const torso = new THREE.InstancedMesh(bakeColored(makeTorsoProto(mats)), mats.baked, count);
  const head = new THREE.InstancedMesh(makeHeadGeometry(), white, count);
  const hair = new THREE.InstancedMesh(
    new THREE.SphereGeometry(VISITOR_HEAD_RADIUS + 0.007, 9, 7, 0, Math.PI * 2, 0, Math.PI * 0.55),
    white,
    count,
  );
  torso.castShadow = true;
  leftLegs.castShadow = false;
  rightLegs.castShadow = false;
  head.castShadow = false;
  hair.castShadow = false;
  group.add(leftLegs, rightLegs, torso, head, hair);

  // One flat disc serves both hats: pulled forward and squeezed for a cap bill, wide and centred for a sun hat.
  const hatOwners = looks.map((look, index) => (look.hat !== 'none' ? index : -1)).filter((index) => index >= 0);
  const brimGeometry = new THREE.CylinderGeometry(0.085, 0.085, 0.02, 14);
  const brims = new THREE.InstancedMesh(brimGeometry, white, Math.max(hatOwners.length, 1));
  brims.castShadow = false;
  brims.count = hatOwners.length;
  group.add(brims);

  const balloonOwners = looks.map((look, index) => (look.balloon != null ? index : -1)).filter((index) => index >= 0);
  const balloons = new THREE.InstancedMesh(new THREE.SphereGeometry(0.11, 9, 7), white, Math.max(balloonOwners.length, 1));
  const strings = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.006, 0.006, 0.5, 4), mats.charcoal, Math.max(balloonOwners.length, 1));
  balloons.castShadow = false;
  strings.castShadow = false;
  balloons.count = balloonOwners.length;
  strings.count = balloonOwners.length;
  group.add(balloons, strings);

  const tint = new THREE.Color();
  looks.forEach((look, index) => {
    leftLegs.setColorAt(index, tint.setHex(look.pants));
    rightLegs.setColorAt(index, tint.setHex(look.pants));
    torso.setColorAt(index, tint.setHex(look.shirt));
    head.setColorAt(index, tint.setHex(look.skin));
    hair.setColorAt(index, tint.setHex(look.hair));
  });
  hatOwners.forEach((owner, slot) => brims.setColorAt(slot, tint.setHex(looks[owner]!.hair)));
  balloonOwners.forEach((owner, slot) => balloons.setColorAt(slot, tint.setHex(looks[owner]!.balloon!)));
  for (const part of [leftLegs, rightLegs, torso, head, hair, brims, balloons]) {
    if (part.instanceColor) part.instanceColor.needsUpdate = true;
  }

  const dummy = new THREE.Object3D();
  const hide = (mesh: THREE.InstancedMesh, index: number) => {
    dummy.position.set(0, -8, 0);
    dummy.rotation.set(0, 0, 0);
    dummy.scale.setScalar(0);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
  };
  const hideGuest = (index: number) => {
    hide(leftLegs, index);
    hide(rightLegs, index);
    hide(torso, index);
    hide(head, index);
    hide(hair, index);
    const hatSlot = hatOwners.indexOf(index);
    if (hatSlot >= 0) hide(brims, hatSlot);
  };

  const write = (
    index: number,
    x: number,
    z: number,
    facing: number,
    bob: number,
    lean: number,
    elapsed: number,
    stride = 0,
  ) => {
    const look = looks[index]!;
    const h = look.height;
    dummy.position.set(x, parkTop + bob, z);
    dummy.rotation.set(lean + stride, facing, 0);
    dummy.scale.setScalar(h);
    dummy.updateMatrix();
    leftLegs.setMatrixAt(index, dummy.matrix);
    dummy.rotation.set(lean - stride, facing, 0);
    dummy.updateMatrix();
    rightLegs.setMatrixAt(index, dummy.matrix);
    dummy.rotation.set(lean, facing, 0);
    dummy.updateMatrix();
    torso.setMatrixAt(index, dummy.matrix);
    dummy.position.y = parkTop + bob + VISITOR_HEAD_Y * h;
    dummy.updateMatrix();
    head.setMatrixAt(index, dummy.matrix);
    dummy.position.y = parkTop + bob + (VISITOR_HEAD_Y + 0.01 + look.hairLift) * h;
    dummy.scale.set(look.hairScale[0] * h, look.hairScale[1] * h, look.hairScale[2] * h);
    dummy.updateMatrix();
    hair.setMatrixAt(index, dummy.matrix);
    if (look.hat !== 'none') {
      const slot = hatOwners.indexOf(index);
      // Local -Z is the visible front; yaw maps it to world (-sin f, 0, -cos f).
      const forward = look.hat === 'cap' ? -VISITOR_CAP_BRIM_Z * h : 0;
      dummy.position.set(x - Math.sin(facing) * forward, parkTop + bob + (VISITOR_HEAD_Y + 0.035) * h, z - Math.cos(facing) * forward);
      if (look.hat === 'cap') {
        dummy.scale.set(0.95 * h, h, 1.2 * h);
      } else {
        dummy.scale.set(1.95 * h, h, 1.95 * h);
      }
      dummy.updateMatrix();
      brims.setMatrixAt(slot, dummy.matrix);
    }
    if (look.balloon != null) {
      const slot = balloonOwners.indexOf(index);
      const sway = Math.sin(elapsed * 1.6 + index) * 0.04;
      const handX = x + Math.cos(facing) * VISITOR_HAND_OFFSET[0] * h;
      const handZ = z - Math.sin(facing) * VISITOR_HAND_OFFSET[0] * h;
      dummy.position.set(handX + sway, parkTop + bob + 0.53 * h, handZ);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.setScalar(h);
      dummy.updateMatrix();
      strings.setMatrixAt(slot, dummy.matrix);
      dummy.position.y = parkTop + bob + 0.86 * h;
      dummy.position.x = handX + sway * 2;
      dummy.updateMatrix();
      balloons.setMatrixAt(slot, dummy.matrix);
    }
  };

  const routePoints = spec.routes.map((route) => route.points.map((point) => ({ x: point.x, z: point.y } as PathPoint)));
  const gaitPhases = spec.routes.map(() => 0);

  group.userData.gaitPhases = gaitPhases;
  group.userData.retargetPatrolRoutes = (routes: Route[]): boolean => {
    if (routes.length !== spec.routes.length) {
      return false;
    }
    for (let i = 0; i < routes.length; i += 1) {
      spec.routes[i] = routes[i]!;
      routePoints[i] = routes[i]!.points.map((point) => ({ x: point.x, z: point.y }));
    }
    return true;
  };
  group.userData.update = (elapsed: number) => {
    spec.routes.forEach((route, index) => {
      const pauseEvery = route.pauseEvery ?? 0;
      const pauseFor = route.pauseFor ?? 0;
      const pausePhase = route.pausePhase ?? 0;
      const walked = walkedTime(elapsed, pauseEvery, pauseFor, pausePhase);
      const points = routePoints[index]!;
      const total = points.reduce((sum, point, i) => {
        const next = points[(i + 1) % points.length]!;
        return sum + Math.hypot(next.x - point.x, next.z - point.z);
      }, 0);
      const distance = route.offset * total + walked * route.speed;
      const along = poseOnClosedPath(points, distance);
      const facing = travelFacing(along.facing, route.speed);
      const offset = applyLaneOffsetOnClosedPath(points, distance, route.lane ?? 0);
      const paused = isPatrolPaused(elapsed, pauseEvery, pauseFor, pausePhase);
      const gait = walked * Math.abs(route.speed) * 9 + index;
      gaitPhases[index] = gait;
      const stride = paused ? 0 : Math.sin(gait) * 0.45;
      const bob = paused ? Math.abs(Math.sin(elapsed * 1.1 + index)) * 0.008 : Math.abs(Math.sin(gait)) * 0.03;
      const lean = paused ? 0 : Math.sin(gait) * 0.03;
      write(index, offset.x, offset.z, routeWalkerVisualYaw(facing), bob, lean, elapsed, stride);
    });
    spec.standing.forEach((stand, offset) => {
      const index = spec.routes.length + offset;
      write(index, stand.x, stand.z, stand.facing + Math.sin(elapsed * 0.7 + offset) * 0.12, Math.abs(Math.sin(elapsed * 1.3 + offset)) * 0.006, 0, elapsed, 0);
    });
    spec.queue.forEach((slot, offset) => {
      const index = spec.routes.length + spec.standing.length + offset;
      if (slot.hidden) {
        hideGuest(index);
        return;
      }
      const sway = Math.sin(elapsed * 1.3 + slot.phase) * 0.03;
      const bob = Math.abs(Math.sin(elapsed * 1.6 + slot.phase)) * 0.01;
      write(index, slot.x + Math.cos(slot.facing) * sway, slot.z - Math.sin(slot.facing) * sway, slot.facing, bob, 0, elapsed, 0);
    });
    for (const part of [leftLegs, rightLegs, torso, head, hair, brims, balloons, strings]) {
      part.instanceMatrix.needsUpdate = true;
    }
  };
  group.userData.update(0, 0);
  return group;
}
